import { Drawer, FormControlLabel, FormGroup, Grid, LinearProgress, Switch } from "@mui/material";
import moment from "moment";
import { useEffect, useState } from "react";
import { ValidatorForm } from "react-material-ui-form-validator";
import { useParams } from "react-router-dom";
import { EmailTypeEnum } from "../../_common/enum/emailType.enum";
import { handleErrorResponse } from "../../_helpers/handleResponse.helper";
import { emailChecklistService } from "../../_services/emailChecklist.service";
import { emailLogService } from "../../_services/emailLog.service";
import { useDrawerStyles } from "../../_styles/drawer.styles";
import { AdminUserAutoComplete } from "../autocomplete/adminUser.autoComplete";
import { EmailTemplateAutoComplete } from "../autocomplete/emailTemplate.autoComplete";
import { PrimaryButton } from "../button/primary.button";
import { DrawerHeader } from "../drawer/header.drawer";
import { TextValidatorHcs } from "../textValidator/textValidatorHcs";
import { toastr } from "../toastr/toastr";
import { TinyMce } from "../tinyMce/tinyMce";
import { emailtemplateService } from "../../_services/emailTemplate.service";

export const EmailLogManageDrawer = (params: { isOpen: boolean, itemId: number | null, onSave(): void, onClose(): void }) => {

    const resetData = () => ({
        id: 0,
        name: "",
        comment: ""
    });
    const { formId } = useParams<{ formId: string }>();
    const [data, setData] = useState<any>(resetData);
    const [checklists, setChecklists] = useState<Array<any>>([]);

    const [isLoading, setIsLoading] = useState(false);
    const [isChecklistLoading, setIsChecklistLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        fetchChecklists()
    }, []);

    useEffect(() => {
        if (!!params.itemId)
            fetchData();
        else
            setData(resetData);
    }, [params.itemId, params.isOpen]);

    useEffect(() => {
        if (!!data.emailTemplateId)
            fetchEmailBody();
    }, [data.emailTemplateId]);

    function fetchEmailBody() {
        setIsLoading(true)
        return emailtemplateService
            .get(data.emailTemplateId)
            .then(
                (json) => {
                    setData((d: any) => ({ ...d, body: json.body }));
                }, handleErrorResponse
            )
            .finally(() => {
                setIsLoading(false);
            });
    }

    function fetchData() {
        if (!params.itemId) return;

        setIsLoading(true)
        return emailLogService
            .get(params.itemId)
            .then(
                (json) => {
                    setData({ ...json, startDate: moment(json.startDate).format('YYYY-MM-DD'), expiryDate: moment(json.expiryDate).format('YYYY-MM-DD') });
                }, handleErrorResponse
            )
            .finally(() => {
                setIsLoading(false);
            });
    }

    function fetchChecklists() {
        setIsChecklistLoading(true)
        return emailChecklistService
            .dropdown()
            .then(
                (json) => {
                    setChecklists(json);
                }, handleErrorResponse
            )
            .finally(() => {
                setIsChecklistLoading(false);
            });
    }

    function submit(e: any) {
        e.preventDefault();
        setIsSaving(true);
        var _checklists = checklists.filter(m => m.isChecked).map(m => m.name);
        var _data = { ...data, formId: formId, "checklists": _checklists };
        return emailLogService
            .submit(_data)
            .then(
                () => {
                    toastr.success("Saved successfully");
                    params.onSave();
                },
                handleErrorResponse
            )
            .finally(() => setIsSaving(false));
    }

    function disable() {
        if (!params.itemId) return;
        setIsLoading(true);
        return emailLogService
            .disable(params.itemId)
            .then(
                (d: any) => {
                    toastr.success("Disabled successfully");
                    fetchData();
                    params.onSave();
                }, handleErrorResponse
            )
            .finally(() => setIsLoading(false));
    }

    function handleChange(event: any) {
        setData((_data: any) => ({ ..._data, [event.target.name]: event.target.value }))
    };

    function handleChangeSwitch(event: any, index: number) {
        setChecklists((__data: Array<any>) => {
            var _return = [...__data];
            (_return[index]).isChecked = !((_return[index]).isChecked || false);
            return [..._return];
        });
    };

    function handleTextareaChange(newContent: string) {
        setData((_data: any) => ({ ..._data, body: newContent }))
    };

    return (
        <Drawer
            anchor={"right"}
            variant="persistent"
            open={params.isOpen}
            ModalProps={{
                keepMounted: true
            }}>

            <div
                tabIndex={0}
                role="button">
                <div>
                    <DrawerHeader name={(!!params.itemId) ? "Update EmailLog" : "Create EmailLog"} onClose={params.onClose} />

                    <ValidatorForm
                        onSubmit={submit}
                        noValidate>

                        <Grid container spacing={1} sx={useDrawerStyles.container}>

                            <Grid item xs={12}>
                                <EmailTemplateAutoComplete
                                    isRequired
                                    parentId={EmailTypeEnum.Checklist}
                                    itemId={data.emailTemplateId}
                                    name={"emailTemplateId"}
                                    onItemChange={handleChange} />
                            </Grid>

                            <Grid item xs={12}>
                                {isChecklistLoading && <LinearProgress />}
                                {(!isChecklistLoading) && <>
                                    <FormGroup>
                                        {(checklists && checklists.length > 0) && checklists.map((m: any, i: number) =>
                                            <FormControlLabel control={<Switch checked={m.isChecked} onChange={(e) => { handleChangeSwitch(e, i) }} name={`chelistItem[${i}]`} />} label={m.name} />
                                        )}
                                    </FormGroup>
                                </>}
                            </Grid>

                            <Grid item xs={12}>
                                <AdminUserAutoComplete
                                    isRequired
                                    itemId={data.senderId}
                                    name={"senderId"}
                                    onItemChange={handleChange} />
                            </Grid>
                            <Grid item xs={12}>
                                <TextValidatorHcs
                                    label="Comment"
                                    value={data.comment}
                                    name="comment"
                                    fullWidth
                                    variant="filled"
                                    onChange={handleChange}
                                />
                            </Grid>

                            {!!data.emailTemplateId &&
                                <Grid item xs={12}>
                                    <TinyMce tagName="Body" value={data.body} onChange={handleTextareaChange} />
                                </Grid>
                            }
                        </Grid>

                        <Grid container spacing={1} sx={useDrawerStyles.container}>

                            <Grid item xs={(data.id === 0 ? 12 : 6)}>
                                <PrimaryButton
                                    isSaving={isSaving}
                                    isLoading={isLoading}
                                    type="submit"
                                    variant="save">{"Save"}
                                </PrimaryButton>
                            </Grid>

                            {data.id > 0 &&
                                <Grid item xs={6}>
                                    <PrimaryButton
                                        type="button"
                                        variant="danger"
                                        disabled={isLoading}
                                        onClick={disable}
                                        icon="block"
                                    >
                                        Disable
                                    </PrimaryButton>
                                </Grid>
                            }

                        </Grid>

                    </ValidatorForm>
                </div>
            </div>
        </Drawer >
    );
};